<script lang="ts">
  // 3 · Work week: hours per week, working days, first day of the week.
  import { Plus, Minus } from '@lucide/svelte';
  import { t } from '../../i18n.svelte';
  import { formatHours } from '../../format';
  import { Chip, ChipGroup, Field, IconButton, Segmented } from '../../ui';
  import StepIntro from './StepIntro.svelte';
  import { flow } from '../state.svelte';

  const HOUR_PRESETS = [24, 32, 36, 40].map((h) => ({ value: h, label: String(h) }));
  const orderedDays = $derived(flow.weekStart === 0 ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5, 6, 0]);
  const perDay = $derived(flow.days.length ? formatHours(flow.hours / flow.days.length) : '—');
</script>

<StepIntro kicker={t('onb.week.kicker')} title={t('onb.week.title')} lead={t('onb.week.text')} />
<div class="stack">
  <Field label={t('onb.week.hours')} for="onb-hours">
    <div class="hours">
      <IconButton label="−" variant="secondary" onclick={() => flow.setHours(flow.hours - 1)}><Minus /></IconButton>
      <div class="hours-input focus-ring">
        <input id="onb-hours" class="tabular" type="number" min="0" max="168" inputmode="numeric" bind:value={flow.hours} onblur={() => flow.setHours(flow.hours)} />
        <span class="unit">{t('onb.week.hours_unit')}</span>
      </div>
      <IconButton label="+" variant="secondary" onclick={() => flow.setHours(flow.hours + 1)}><Plus /></IconButton>
    </div>
    <ChipGroup options={HOUR_PRESETS} selected={[flow.hours]} ariaLabel={t('onb.week.hours')} onchange={(v) => flow.setHours(v[0] ?? flow.hours)} />
  </Field>

  <Field label={t('onb.week.days')} error={flow.daysError ? t('onb.week.no_days') : null} hint={t('onb.week.per_day', { hours: perDay })}>
    <ChipGroup ariaLabel={t('onb.week.days')}>
      {#each orderedDays as d (d)}
        <Chip label={t(`onb.day.${d}`)} ariaLabel={t(`onb.day_long.${d}`)} selected={flow.days.includes(d)} onclick={() => flow.toggleDay(d)} />
      {/each}
    </ChipGroup>
  </Field>

  <Field label={t('onb.week.start')}>
    <Segmented
      value={flow.weekStart}
      options={[{ value: 1, label: t('onb.day_long.1') }, { value: 0, label: t('onb.day_long.0') }]}
      size="sm"
      ariaLabel={t('onb.week.start')}
      onchange={(v) => (flow.weekStart = v)}
    />
  </Field>
</div>

<style>
  .hours { display: flex; align-items: center; gap: var(--space-2_5); }
  .hours-input {
    display: flex;
    align-items: baseline;
    gap: var(--space-1_5);
    height: var(--control-h-xl);
    padding: 0 var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
  }
  .hours-input:focus-within { border-color: var(--brand); }
  input {
    width: 3.2ch;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--text);
    font-size: var(--text-3xl);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    line-height: var(--control-h-xl);
    text-align: right;
    outline: none;
    appearance: textfield;
    -moz-appearance: textfield;
  }
  input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .unit { font-size: var(--text-md); font-weight: var(--weight-medium); color: var(--text-3); }
</style>
